import { BlockType } from '../lib/types';

export type BpmnSymbolId =
  | 'noneStartEvent'
  | 'messageStartEvent'
  | 'timerStartEvent'
  | 'noneEndEvent'
  | 'terminateEndEvent'
  | 'intermediateTimerEvent'
  | 'task'
  | 'userTask'
  | 'serviceTask'
  | 'subProcess'
  | 'callActivity'
  | 'exclusiveGateway'
  | 'parallelGateway'
  | 'inclusiveGateway'
  | 'eventBasedGateway'
  | 'dataObject'
  | 'dataStore'
  | 'textAnnotation';

export type BpmnCategoryId = 'events' | 'activities' | 'gateways' | 'artifacts';

export interface BpmnSymbolDefinition {
  id: BpmnSymbolId;
  label: string;
  xmlType: string;
  category: BpmnCategoryId;
  /** Maps to existing swimlane block type for rendering & layers */
  blockType: BlockType;
  defaultText: string;
  /** Famla colour token applied at render time */
  colorToken: 'green' | 'red' | 'orange' | 'purple' | 'neutral';
  /** Matching filename on https://iconmagic.io/iconset/bpmn */
  iconMagicName: string;
  draggable: boolean;
}

export interface BpmnCategoryDefinition {
  id: BpmnCategoryId;
  label: string;
  symbols: BpmnSymbolDefinition[];
}

const symbol = (
  id: BpmnSymbolId,
  label: string,
  xmlType: string,
  category: BpmnCategoryId,
  blockType: BlockType,
  colorToken: BpmnSymbolDefinition['colorToken'],
  iconMagicName: string,
  defaultText?: string
): BpmnSymbolDefinition => ({
  id,
  label,
  xmlType,
  category,
  blockType,
  colorToken,
  iconMagicName,
  defaultText: defaultText ?? label,
  draggable: true,
});

export const BPMN_SYMBOLS: Record<BpmnSymbolId, BpmnSymbolDefinition> = {
  noneStartEvent: symbol('noneStartEvent', 'None Start Event', 'bpmn:startEvent', 'events', 'start', 'green', 'start-event-none'),
  messageStartEvent: symbol('messageStartEvent', 'Message Start Event', '+ bpmn:messageEventDefinition', 'events', 'start', 'green', 'start-event-message'),
  timerStartEvent: symbol('timerStartEvent', 'Timer Start Event', '+ bpmn:timerEventDefinition', 'events', 'start', 'green', 'start-event-timer'),
  noneEndEvent: symbol('noneEndEvent', 'None End Event', 'bpmn:endEvent', 'events', 'end', 'red', 'end-event-none'),
  terminateEndEvent: symbol('terminateEndEvent', 'Terminate End Event', '+ bpmn:terminateEventDefinition', 'events', 'end', 'red', 'end-event-terminate'),
  intermediateTimerEvent: symbol('intermediateTimerEvent', 'Intermediate Timer Event', 'bpmn:intermediateCatchEvent', 'events', 'start', 'green', 'intermediate-event-catch-timer', 'Wait'),
  task: symbol('task', 'Task', 'bpmn:task', 'activities', 'process', 'orange', 'task'),
  userTask: symbol('userTask', 'User Task', 'bpmn:userTask', 'activities', 'process', 'orange', 'user-task'),
  serviceTask: symbol('serviceTask', 'Service Task', 'bpmn:serviceTask', 'activities', 'process', 'orange', 'service-task'),
  subProcess: symbol('subProcess', 'Subprocess', 'bpmn:subProcess', 'activities', 'process', 'orange', 'subprocess-collapsed'),
  callActivity: symbol('callActivity', 'Call Activity', 'bpmn:callActivity', 'activities', 'process', 'orange', 'call-activity'),
  exclusiveGateway: symbol('exclusiveGateway', 'Exclusive Gateway (XOR)', 'bpmn:exclusiveGateway', 'gateways', 'decision', 'orange', 'gateway-xor'),
  parallelGateway: symbol('parallelGateway', 'Parallel Gateway (AND)', 'bpmn:parallelGateway', 'gateways', 'decision', 'orange', 'gateway-parallel'),
  inclusiveGateway: symbol('inclusiveGateway', 'Inclusive Gateway (OR)', 'bpmn:inclusiveGateway', 'gateways', 'decision', 'orange', 'gateway-or'),
  eventBasedGateway: symbol('eventBasedGateway', 'Event-based Gateway', 'bpmn:eventBasedGateway', 'gateways', 'decision', 'orange', 'gateway-eventbased'),
  dataObject: symbol('dataObject', 'Data Object', 'bpmn:dataObject', 'artifacts', 'note', 'neutral', 'data-object'),
  dataStore: symbol('dataStore', 'Data Store', 'bpmn:dataStoreReference', 'artifacts', 'note', 'neutral', 'data-store'),
  textAnnotation: symbol('textAnnotation', 'Text Annotation', 'bpmn:textAnnotation', 'artifacts', 'note', 'neutral', 'text-annotation', 'text annotation'),
};

export const BPMN_CATEGORIES: BpmnCategoryDefinition[] = [
  {
    id: 'events',
    label: 'Events',
    symbols: [
      BPMN_SYMBOLS.noneStartEvent,
      BPMN_SYMBOLS.messageStartEvent,
      BPMN_SYMBOLS.timerStartEvent,
      BPMN_SYMBOLS.noneEndEvent,
      BPMN_SYMBOLS.terminateEndEvent,
      BPMN_SYMBOLS.intermediateTimerEvent,
    ],
  },
  {
    id: 'activities',
    label: 'Activities',
    symbols: [
      BPMN_SYMBOLS.task,
      BPMN_SYMBOLS.userTask,
      BPMN_SYMBOLS.serviceTask,
      BPMN_SYMBOLS.subProcess,
      BPMN_SYMBOLS.callActivity,
    ],
  },
  {
    id: 'gateways',
    label: 'Gateways',
    symbols: [
      BPMN_SYMBOLS.exclusiveGateway,
      BPMN_SYMBOLS.parallelGateway,
      BPMN_SYMBOLS.inclusiveGateway,
      BPMN_SYMBOLS.eventBasedGateway,
    ],
  },
  {
    id: 'artifacts',
    label: 'Artifacts',
    symbols: [
      BPMN_SYMBOLS.dataObject,
      BPMN_SYMBOLS.dataStore,
      BPMN_SYMBOLS.textAnnotation,
    ],
  },
];

export function getBpmnSymbol(id: BpmnSymbolId | string | undefined): BpmnSymbolDefinition | undefined {
  if (!id) return undefined;
  return BPMN_SYMBOLS[id as BpmnSymbolId];
}

export function isBpmnSymbolId(value: string): value is BpmnSymbolId {
  return value in BPMN_SYMBOLS;
}

export function getBpmnDropDimensions(symbol: BpmnSymbolDefinition): { width: number; height: number } {
  switch (symbol.category) {
    case 'events':
    case 'gateways':
      return { width: 80, height: 80 };
    case 'activities':
      // IconMagic activity glyphs are ~6:5 (36×30 / 32×28). A 2:1 box (120×60)
      // letterboxed the rounded rect inside the hit area.
      if (symbol.id === 'task' || symbol.id === 'subProcess') {
        return { width: 120, height: 105 };
      }
      return { width: 120, height: 100 };
    case 'artifacts':
      if (symbol.id === 'dataObject') return { width: 60, height: 80 };
      if (symbol.id === 'textAnnotation') return { width: 140, height: 50 };
      return { width: 80, height: 80 };
    default:
      return { width: 120, height: 100 };
  }
}
